import axiosInstance from './axios';

// Assessment API interfaces
export interface AssessmentTemplate {
  templateId: string;
  templateName: string;
  version: string;
  questions: AssessmentQuestion[];
  instructions: string;
  estimatedTime: number;
  isRequired: boolean;
}

export interface AssessmentQuestion {
  questionId: string;
  questionText: string;
  questionType: 'likert_scale' | 'multiple_choice' | 'long_text' | 'file_upload';
  options?: string[];
  scoreValues?: number[];
  required: boolean;
  helpText?: string;
}

export interface PatientAssessment {
  assessmentId: string;
  appointmentId: string;
  patientEmail: string;
  templateId: string;
  responses: Record<string, string>;
  totalScore?: number;
  scoreLevel?: string;
  scoreInterpretation?: string;
  status: 'assigned' | 'in_progress' | 'completed';
  progress: number;
  assignedAt: string;
  startedAt?: string;
  completedAt?: string;
}

export interface AssessmentPortalData {
  appointment: {
    appointmentId: string;
    patientEmail: string;
    dateTime: string;
    serviceType: string;
  };
  assessments: PatientAssessment[];
}

export interface SaveResponsesRequest {
  responses: Record<string, string>;
  progress: number;
  status?: string;
}

export interface SubmitAssessmentRequest {
  responses: Record<string, string>;
  totalScore?: number;
  scoreLevel?: string;
  scoreInterpretation?: string;
}

// Assessment API service
export const assessmentAPI = {
  // Get assessment portal data
  getAssessmentPortal: (appointmentId: string) =>
    axiosInstance.get<AssessmentPortalData>(`/assessments/portal/${appointmentId}`),

  // Get specific assessment
  getAssessment: (assessmentId: string) =>
    axiosInstance.get<PatientAssessment>(`/assessments/${assessmentId}`),

  // Get assessment template
  getAssessmentTemplate: (assessmentId: string) =>
    axiosInstance.get<AssessmentTemplate>(`/assessments/${assessmentId}/template`),

  // Save responses (auto-save)
  saveResponses: (assessmentId: string, data: SaveResponsesRequest) =>
    axiosInstance.post(`/assessments/${assessmentId}/responses`, data),

  // Submit completed assessment
  submitAssessment: (assessmentId: string, data: SubmitAssessmentRequest) =>
    axiosInstance.post(`/assessments/${assessmentId}/submit`, data),

  // Track portal access
  trackAccess: (assessmentId: string) =>
    axiosInstance.post(`/assessments/${assessmentId}/access`),
};

// Utility functions
export const calculatePHQ9Score = (responses: Record<string, string>): { totalScore: number; level: string; interpretation: string } => {
  const phq9Questions = Object.entries(responses)
    .filter(([key]) => key.startsWith('phq9_q'))
    .map(([_, value]) => parseInt(value) || 0);

  const totalScore = phq9Questions.reduce((sum, score) => sum + score, 0);

  let level: string;
  let interpretation: string;

  if (totalScore <= 4) {
    level = 'minimal';
    interpretation = 'Minimal depression symptoms - monitor';
  } else if (totalScore <= 9) {
    level = 'mild';
    interpretation = 'Mild depression - consider counseling/therapy';
  } else if (totalScore <= 14) {
    level = 'moderate';
    interpretation = 'Moderate depression - active treatment recommended';
  } else if (totalScore <= 19) {
    level = 'moderate-severe';
    interpretation = 'Moderately severe depression - active treatment required';
  } else {
    level = 'severe';
    interpretation = 'Severe depression - immediate treatment required';
  }

  return { totalScore, level, interpretation };
};

export const calculateGAD7Score = (responses: Record<string, string>): { totalScore: number; level: string; interpretation: string } => {
  const gad7Questions = Object.entries(responses)
    .filter(([key]) => key.startsWith('gad7_q'))
    .map(([_, value]) => parseInt(value) || 0);

  const totalScore = gad7Questions.reduce((sum, score) => sum + score, 0);

  let level: string;
  let interpretation: string;

  if (totalScore <= 4) {
    level = 'minimal';
    interpretation = 'Minimal anxiety symptoms - monitor';
  } else if (totalScore <= 9) {
    level = 'mild';
    interpretation = 'Mild anxiety - consider counseling/therapy';
  } else if (totalScore <= 14) {
    level = 'moderate';
    interpretation = 'Moderate anxiety - active treatment recommended';
  } else {
    level = 'severe';
    interpretation = 'Severe anxiety - immediate treatment required';
  }

  return { totalScore, level, interpretation };
};

export const calculateProgress = (responses: Record<string, string>, template: AssessmentTemplate): number => {
  const requiredQuestions = template.questions.filter(q => q.required);
  const answeredQuestions = requiredQuestions.filter(q => responses[q.questionId]?.trim());
  return requiredQuestions.length > 0 ? answeredQuestions.length / requiredQuestions.length : 0;
};

export const isAssessmentComplete = (responses: Record<string, string>, template: AssessmentTemplate): boolean => {
  const requiredQuestions = template.questions.filter(q => q.required);
  return requiredQuestions.every(q => responses[q.questionId]?.trim());
};